import * as R from 'ramda';
import { pure, compose, withState, withHandlers } from 'react-recompose';
// helpers/constants
import * as G from '../helpers';
import * as GC from '../constants';
import { ENUMS } from '../constants/enums';
// utilities
import { sendRequest } from '../utilities/http';
import endpointsMap from '../utilities/endpoints';
//////////////////////////////////////////////////

const CONTACT_FORM_SECTION = 'CONTACT_FORM_SECTION';

const getBranchProps = (mainProps, props) => {
  if (G.isTrue(R.path(['shouldGetLocationTemplateSearchRequestProps'], mainProps))) {
    return G.getAsyncSearchTemplateAutocompleteRequestProps(props);
  }
  const { branchGuid } = props;
  const branchGuidToUse = G.ifElse(
    G.isTrue(R.path(['useCurrentBranch'], mainProps)),
    G.getAmousCurrentBranchGuidFromWindow(),
    branchGuid,
  );

  return {
    [GC.FIELD_BRANCH_GUID]: branchGuidToUse,
  };
};

export const withAsyncSearchTemplateAutocomplete = (mainProps) => compose(
  withState('templateItems', 'setTemplateItems', null),
  withState('templateIdOptions', 'setTemplateIdOptions', []),
  withHandlers({
    handleGetLoadOptions: (props) => async (inputValue, callback) => {
      const { setTemplateItems, setTemplateIdOptions } = props;

      if (G.isNotNilAndNotEmpty(inputValue)) {
        const data = {
          ...getBranchProps(mainProps, props),
          [GC.FIELD_BILL_TO]: G.getPropFromObject('isBillTo', mainProps),
          groupCriteriaList: [{
            value: inputValue,
            propertyNames: [
              GC.FIELD_TEMPLATE_ID,
              GC.FIELD_LOCATION_NAME,
              GC.FIELD_ADDRESS_1,
              GC.FIELD_CITY,
              GC.FIELD_STATE,
            ],
          }],
        };

        const result = await sendRequest('post', endpointsMap.listLocations, { data });

        if (G.isResponseSuccess(result.status)) {
          const resData = R.compose(
            R.map((template) => ({
              ...template,
              [GC.FIELD_STATE]: G.getStateValueByConfig(R.prop(GC.FIELD_STATE, template)),
            })),
            R.pathOr([], ['data']),
          )(result);

          setTemplateItems(resData);

          const options = R.map(({ zip, city, state, guid, address1, templateId, locationName }) => (
            {
              templateId,
              value: guid,
              label: `${templateId}, ${locationName} (${R.trim(address1)}, ${city}, ${
                G.getStateValueByConfig(state)}, ${zip})`,
            }
          ), R.pathOr([], ['data'], result));

          setTemplateIdOptions(options);
          callback(options);
        } else {
          G.handleFailResponseSimple(result);
        }
      } else {
        callback([]);
      }
    },
    // NOTE: for redux-form
    handleAsyncCreatableSearchChange: (props) => (field) => {
      const { form, change, withFormName, withContactsAutocomplete, handleCalculateDistances } = props;

      G.changeStoreField(change, form, GC.FIELD_TEMPLATE_ID, field, withFormName);

      const searchedTemplateId = R.filter((item) => (
        R.equals(R.prop(GC.FIELD_GUID, item), R.propOr('', 'value', field))
      ), R.pathOr([], ['templateItems'], props));

      if (R.and(G.isNilOrEmpty(searchedTemplateId), G.isNotNilAndNotEmpty(field))) return;

      R.forEachObjIndexed((templateItem, templateKey) => {
        if (R.and(withContactsAutocomplete, R.equals(templateKey, GC.FIELD_CONTACTS))) {
          R.forEachObjIndexed((contactItem, contactKey) =>
            G.changeStoreField(change, CONTACT_FORM_SECTION, contactKey, contactItem, withFormName),
          R.pathOr(GC.contactDefaultFields, ['0'], templateItem));
        }

        if (R.equals(templateKey, GC.FIELD_LOCATION_TYPE)) {
          const templateType = {
            label: R.propOr(null, GC.FIELD_DISPLAYED_VALUE, templateItem),
            value: R.propOr(null, GC.FIELD_DROPDOWN_OPTION_GUID, templateItem),
          };

          G.changeStoreField(change, form, templateKey, templateType, withFormName);
        }

        G.changeStoreField(change, form, templateKey, templateItem, withFormName);
      }, R.pick(R.keys(GC.locationDefaultFields), R.pathOr([], ['0'], searchedTemplateId)));

      if (G.isFunction(handleCalculateDistances)) {
        handleCalculateDistances();
      }
    },
    // NOTE: for formik
    handleAsyncSearchSelectChange: (props) => (field) => {
      const { values, setValues, templateItems } = props;

      const eventType = R.pathOr(R.path([GC.FIELD_EVENT_TYPE], props), [GC.FIELD_EVENT_TYPE], values);

      if (G.isNilOrEmpty(field)) return;

      const template = R.find(R.propEq(field.value, GC.FIELD_GUID), R.or(templateItems, []));

      if (G.isNilOrEmpty(template)) return;

      const { templateId, dropTrailer } = template;

      const branchConfigs = G.getItemFromWindow('amousNewDoBranchConfigs');
      const manageTrailersFrom = G.getConfigValueFromStore(GC.TRAILER_MANAGE_TRAILERS_FROM, branchConfigs);
      const useStopTrailers = R.equals(manageTrailersFrom, ENUMS.ENUM_EVENTS) || dropTrailer;

      const trailerFields = useStopTrailers ? {} : {
        [GC.FIELD_STOP_DROPPED_TRAILER_GUIDS]: [],
        [GC.FIELD_STOP_PICKED_UP_TRAILER_GUIDS]: [],
      };

      const newValues = R.compose(
        G.mapLocationHours,
        R.mergeRight({ ...values, eventType }),
        G.mapSearchedLocationWithContactsArr,
        R.mergeLeft({
          ...trailerFields,
          templateId,
          dropTrailer,
          [GC.FIELD_TEMPLATE_GUID]: G.getGuidFromObject(field),
          isAppointment: G.isNotNilAndNotEmpty(R.prop(GC.FIELD_APPOINTMENT_HOUR, template)),
        }),
        R.assoc(GC.FIELD_TEMPLATE_ID, R.prop(GC.FIELD_TEMPLATE_ID, field)),
      )(template);

      const contacts = R.prop(GC.FIELD_CONTACTS, newValues);

      if (G.isNotNilAndNotEmpty(contacts)) {
        const firstContact = R.head(contacts);
        const email = R.prop(GC.FIELD_EMAIL, firstContact);

        const emails = G.ifElse(
          G.isNotNilAndNotEmpty(email),
          R.of([], email),
          [],
        );

        const updatedContact = R.compose(
          R.assoc(GC.FIELD_EMAILS, emails),
          R.omit([GC.FIELD_EMAIL]),
        )(firstContact);

        return setValues(R.mergeRight(newValues, updatedContact));
      }

      return setValues(newValues);
    },
  }),
  pure,
);
